import queryString from 'query-string';
import { DateTime } from 'luxon';
import { DATES_ARRAY, isCustomRangeId } from 'services/constants/dates';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { ESortDirection, TOrderBy } from 'services/constants/types';

export type TNavigateParams = queryString.ParsedQuery<string>;

export type TFilterOperator = '<>' | '=' | '<' | '>' | '<=' | '>=' | 'like';

export type TNavigateFilter = {
	fieldName: string;
	value: string;
	operator: TFilterOperator;
};

/**
 * Builds the filters for one column. Return `null` to fall back to the default handling.
 */
export type TNavigateFilterBuilder = (
	value: string,
	column: TTableStructure<string>,
) => TNavigateFilter[] | null;

export type TGetFiltersOptions = {
	/** Custom filter builders keyed by the column selector. */
	customFilters?: Record<string, TNavigateFilterBuilder>;
};

export type TGetOrderByOptions = {
	defaultFieldName?: string;
	defaultDirection?: ESortDirection;
};

const getParamValue = (navigateParams: TNavigateParams, key: string): string | null => {
	const rawValue = navigateParams[key];
	const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
	if (value === undefined || value === null || value === '') return null;
	return value;
};

/**
 * A column with `separateFilter` has its own control in the filter panel (`SeparateFilter`),
 * which keeps the value in the `f-<selector>` param instead of `<selector>`.
 */
const getColumnFilterValue = (
	navigateParams: TNavigateParams,
	column: TTableStructure<string>,
): string | null =>
	getParamValue(navigateParams, column.selector) ??
	(column.separateFilter ? getParamValue(navigateParams, `f-${column.selector}`) : null);

/**
 * The end of a custom range is inclusive, but the values are compared against timestamps,
 * so the upper bound is the beginning of the next day — the same way the periods of
 * `DATES_ARRAY` use the beginning of the next period as their `to`.
 */
const getRangeEndValue = (endDate: string): string =>
	DateTime.fromISO(endDate).plus({ days: 1 }).toISODate() ?? endDate;

/**
 * Custom range keeps its bounds in the `<selector>StartDate` / `<selector>EndDate` params
 * (see `DateRangeModal`), the column param itself only holds `custom_range`.
 */
const getCustomRangeFilters = (
	fieldName: string,
	navigateParams: TNavigateParams,
): TNavigateFilter[] => {
	const filters: TNavigateFilter[] = [];
	const startDate = getParamValue(navigateParams, `${fieldName}StartDate`);
	const endDate = getParamValue(navigateParams, `${fieldName}EndDate`);

	if (startDate) {
		filters.push({ fieldName, value: startDate, operator: '>=' });
	}
	if (endDate) {
		filters.push({ fieldName, value: getRangeEndValue(endDate), operator: '<=' });
	}
	return filters;
};

/**
 * Builds the `filters` part of a `IRequestWithPageSortFilterSearch` request from the url params:
 * every filterable column of the table structure whose selector is present in the url becomes
 * a filter. Date columns are expanded into a `>=` / `<=` pair of the selected period.
 */
export const getFiltersFromNavigateParams = (
	tableStructure: TTableStructure<string>[],
	navigateParams: TNavigateParams,
	options: TGetFiltersOptions = {},
): TNavigateFilter[] =>
	tableStructure.reduce((acc, column) => {
		const fieldName = column.selector;
		const value = getColumnFilterValue(navigateParams, column);
		if (value === null) return acc;

		const customFilters = options.customFilters?.[fieldName]?.(value, column);
		if (customFilters) {
			acc.push(...customFilters);
			return acc;
		}

		// Date periods are recognized by the value, not by the column type: the very same
		// period ids are used by every date column, whatever its type is (DATE, DATE_TIME, ...).
		if (isCustomRangeId(value)) {
			acc.push(...getCustomRangeFilters(fieldName, navigateParams));
			return acc;
		}

		const dates = DATES_ARRAY.find((item) => item.id === value);
		if (dates) {
			if (dates.from) {
				acc.push({ fieldName, value: dates.from, operator: '>=' });
			}
			if (dates.to) {
				acc.push({ fieldName, value: dates.to, operator: '<=' });
			}
			return acc;
		}

		acc.push({ fieldName, value, operator: '=' });
		return acc;
	}, [] as TNavigateFilter[]);

/**
 * Builds the `orderBy` part of a `IRequestWithPageSortFilterSearch` request from the
 * `sortIndex` / `sortDirection` url params.
 */
export const getOrderByFromNavigateParams = (
	tableStructure: TTableStructure<string>[],
	navigateParams: TNavigateParams,
	options: TGetOrderByOptions = {},
): TOrderBy[] => {
	const { defaultFieldName = 'id', defaultDirection = ESortDirection.DESC } = options;

	const sortIndexParam = getParamValue(navigateParams, 'sortIndex');
	const sortIndex = sortIndexParam !== null ? Number(sortIndexParam) : null;
	const sortedColumn =
		sortIndex !== null && Number.isInteger(sortIndex) ? tableStructure[sortIndex] : undefined;

	const sortDirection = getParamValue(navigateParams, 'sortDirection');

	return [
		{
			fieldName: sortedColumn?.selector ?? defaultFieldName,
			direction:
				sortDirection === ESortDirection.ASC
					? ESortDirection.ASC
					: sortDirection === ESortDirection.DESC
						? ESortDirection.DESC
						: defaultDirection,
		},
	];
};

/**
 * Filter shape of the table api requests (`filter` + `orderBy` as an array of strings),
 * used by the orders / refunds / vat corrections tables.
 */
export type TTableApiFilter = {
	name: string;
	value: string | number | string[] | number[];
	comparator?: string;
};

/** `getFiltersFromNavigateParams` in the table api format. */
export const getTableApiFiltersFromNavigateParams = (
	tableStructure: TTableStructure<string>[],
	navigateParams: TNavigateParams,
	options: TGetFiltersOptions = {},
): TTableApiFilter[] =>
	getFiltersFromNavigateParams(tableStructure, navigateParams, options).map(
		({ fieldName, value, operator }) =>
			operator === '='
				? { name: fieldName, value }
				: { name: fieldName, value, comparator: operator },
	);

/** `getOrderByFromNavigateParams` in the table api format, e.g. `['timestamp desc']`. */
export const getTableApiOrderByFromNavigateParams = (
	tableStructure: TTableStructure<string>[],
	navigateParams: TNavigateParams,
	options: TGetOrderByOptions = {},
): string[] =>
	getOrderByFromNavigateParams(tableStructure, navigateParams, options).map(
		({ fieldName, direction }) => `${fieldName} ${direction}`,
	);

/**
 * `status=Not Fixed` means "everything except Fixed" — used by the shipment tables.
 */
export const notFixedStatusFilterBuilder: TNavigateFilterBuilder = (value) =>
	value === 'Not Fixed' ? [{ fieldName: 'status', value: 'Fixed', operator: '<>' }] : null;
